import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ToggleOff as DisableIcon,
  ToggleOn as EnableIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import StoreCreationBlockedAlert from './StoreCreationBlockedAlert';
import ProCrownIcon from '../billing/ProCrownIcon';
import { useProUpgrade } from '../../context/ProUpgradeContext';
import { DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { formatStoreSlugHost } from '../../utils/storeSlugUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const WEEKDAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const outlinedGoldButtonSx = {
  borderColor: BRAND.accent,
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '12px',
  minHeight: 44,
  width: { xs: '100%', sm: 'auto' },
  px: 2,
  whiteSpace: 'normal',
  lineHeight: 1.3,
  '&:hover': {
    borderColor: BRAND.accentDark,
    backgroundColor: brandAlpha('accent', 0.1),
  },
};

const valueSx = {
  fontWeight: 600,
  wordBreak: 'break-word',
};

const contactLabelKey = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (normalized.includes('email')) return 'storeRegistration.contactInfo.email';
  if (normalized.includes('phone')) return 'storeRegistration.contactInfo.phone';
  if (normalized.includes('website')) return 'storeRegistration.contactInfo.website';
  return null;
};

const StoresMainPanel = ({
  store,
  loading,
  error,
  canUpdate,
  canCreateStore,
  isCreationBlocked,
  billingSetupPath,
  creationBlockTitleKey,
  creationBlockMessageKey,
  creationBlockActionKey,
  actionLoading,
  onRetry,
  onEdit,
  onToggleStatus,
  onCopyBookingLink,
  addStoreProLocked = false,
}) => {
  const { t } = useTranslation();
  const { openProUpgrade } = useProUpgrade();
  const storeAddBlocked = isCreationBlocked && !addStoreProLocked;

  const translateWeekday = (weekday) => {
    const weekdayMap = {
      Monday: t('common.days.monday'),
      Tuesday: t('common.days.tuesday'),
      Wednesday: t('common.days.wednesday'),
      Thursday: t('common.days.thursday'),
      Friday: t('common.days.friday'),
      Saturday: t('common.days.saturday'),
      Sunday: t('common.days.sunday'),
    };
    return weekdayMap[weekday] || weekday;
  };

  if (loading && !store) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={onRetry} startIcon={<RefreshIcon />} variant="outlined">
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  if (!store) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('stores.error.notFound')}</Alert>
      </Box>
    );
  }

  const openingHours = [...(store.openingHours || [])].sort(
    (left, right) => WEEKDAY_ORDER.indexOf(left.weekDay) - WEEKDAY_ORDER.indexOf(right.weekDay),
  );
  const contacts = store.contacts || [];
  const registerPath = storeAddBlocked
    ? billingSetupPath || '/management/stores/register'
    : '/management/stores/register';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3, md: 4 }, minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.25, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={onCopyBookingLink}
            sx={outlinedGoldButtonSx}
          >
            {t('stores.actions.copyBookingLink')}
          </Button>
          {canCreateStore && addStoreProLocked && (
            <PrimaryActionButton
              onClick={openProUpgrade}
              startIcon={<AddIcon />}
              endIcon={<ProCrownIcon size={12} />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('stores.addNew')}
            </PrimaryActionButton>
          )}
          {canCreateStore && !addStoreProLocked && (
            <PrimaryActionButton
              component={Link}
              to={registerPath}
              startIcon={<AddIcon />}
              disabled={storeAddBlocked && !billingSetupPath}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {storeAddBlocked ? t(creationBlockActionKey) : t('stores.addNew')}
            </PrimaryActionButton>
          )}
        </Box>
        {canUpdate && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              alignSelf: { xs: 'flex-end', sm: 'center' },
              justifyContent: 'flex-end',
            }}
          >
            <Tooltip title={t('stores.actions.edit')}>
              <IconButton onClick={onEdit} sx={{ color: BRAND.accentText }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={store.disabled ? t('stores.actions.enable') : t('stores.actions.disable')}>
              <IconButton
                onClick={onToggleStatus}
                disabled={actionLoading}
                sx={{ color: store.disabled ? '#f44336' : '#4caf50' }}
              >
                {actionLoading ? <CircularProgress size={24} /> : (store.disabled ? <EnableIcon /> : <DisableIcon />)}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {canCreateStore && storeAddBlocked && (
        <Box sx={{ mb: 3 }}>
          <StoreCreationBlockedAlert
            billingSetupPath={billingSetupPath}
            titleKey={creationBlockTitleKey}
            messageKey={creationBlockMessageKey}
            actionKey={creationBlockActionKey}
          />
        </Box>
      )}

      <Box
        sx={{
          border: `1px solid ${BRAND.line}`,
          borderRadius: 2,
          p: { xs: 1.5, sm: 3 },
          backgroundColor: BRAND.background,
          minWidth: 0,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            {t('stores.view.basicInfo')}
          </Typography>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">{t('stores.view.name')}:</Typography>
              <Typography variant="body1" sx={valueSx}>{store.name}</Typography>
            </Grid>
            {store.slug && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">{t('stores.view.slug')}:</Typography>
                <Typography variant="body1" sx={valueSx}>{formatStoreSlugHost(store.slug)}</Typography>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">{t('storeRegistration.basicInfo.currency')}:</Typography>
              <Typography variant="body1" sx={valueSx}>{store.currency || DEFAULT_CURRENCY}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">{t('stores.view.status')}:</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={store.disabled ? t('stores.inactive') : t('stores.active')}
                  color={store.disabled ? 'error' : 'success'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Grid>
            {store.about && Object.keys(store.about).length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">{t('stores.view.about')}:</Typography>
                <Box sx={{ mt: 1 }}>
                  {Object.entries(store.about).map(([lang, text]) => (
                    <Box key={lang} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: BRAND.accentText }}>
                        {String(lang).toUpperCase()}:
                      </Typography>
                      <Typography variant="body1" sx={{ ...valueSx, ml: 0 }}>
                        {text || t('common.notProvided')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
            {store.supportedLanguages && store.supportedLanguages.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">{t('stores.view.supportedLanguages')}:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {store.supportedLanguages.map((lang) => (
                    <Chip
                      key={lang}
                      label={String(lang).toUpperCase()}
                      size="small"
                      sx={{ backgroundColor: BRAND.accent, color: BRAND.onAccent }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            {store.storeLanguage && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">{t('stores.view.storeLanguage')}:</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={t(`common.languages.${String(store.storeLanguage).toLowerCase()}`)}
                    size="small"
                    sx={{ backgroundColor: brandAlpha('text', 0.08), color: BRAND.text, fontWeight: 600 }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            {t('stores.view.contact')}
          </Typography>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {contacts.length > 0 ? contacts.map((contact) => {
              const labelKey = contactLabelKey(contact.type);
              return (
                <Grid size={{ xs: 12, sm: 6 }} key={`${contact.type}-${contact.value}`}>
                  <Typography variant="body2" color="text.secondary">
                    {labelKey ? t(labelKey) : contact.type}:
                  </Typography>
                  <Typography variant="body1" sx={valueSx}>
                    {contact.value || t('common.notProvided')}
                  </Typography>
                </Grid>
              );
            }) : (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">{t('common.notProvided')}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            {t('stores.view.address')}
          </Typography>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {store.address ? (
              <>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">{t('stores.view.street')}:</Typography>
                  <Typography variant="body1" sx={valueSx}>{store.address.street}</Typography>
                </Grid>
                {store.address.door && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('stores.view.door')}:</Typography>
                    <Typography variant="body1" sx={valueSx}>{store.address.door}</Typography>
                  </Grid>
                )}
                {store.address.additionalInfo && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">{t('stores.view.additionalInfo')}:</Typography>
                    <Typography variant="body1" sx={valueSx}>{store.address.additionalInfo}</Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('stores.view.city')}:</Typography>
                  <Typography variant="body1" sx={valueSx}>{store.address.city}</Typography>
                </Grid>
                {store.address.state && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">{t('stores.view.state')}:</Typography>
                    <Typography variant="body1" sx={valueSx}>{store.address.state}</Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('stores.view.zipCode')}:</Typography>
                  <Typography variant="body1" sx={valueSx}>{store.address.zipCode}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">{t('stores.view.country')}:</Typography>
                  <Typography variant="body1" sx={valueSx}>{store.address.country}</Typography>
                </Grid>
              </>
            ) : (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">{t('common.notProvided')}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ color: BRAND.accentText, fontWeight: 600, mb: 2, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            {t('stores.view.openingHours')}
          </Typography>
          {openingHours.length > 0 ? (
            <Box>
              {openingHours.map((hours) => (
                <Box
                  key={hours.weekDay}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: { sm: 'space-between' },
                    alignItems: { sm: 'baseline' },
                    gap: { xs: 0.25, sm: 1.5 },
                    mb: { xs: 1, sm: 0.75 },
                    minWidth: 0,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text, flexShrink: 0 }}>
                    {translateWeekday(hours.weekDay)}
                  </Typography>
                  <Typography variant="body2" sx={{ ...valueSx, textAlign: { xs: 'left', sm: 'right' } }}>
                    {hours.periods && hours.periods.length > 0
                      ? hours.periods.map((period) => `${period.from} – ${period.to}`).join(', ')
                      : t('common.closed')}
                  </Typography>
                </Box>
              ))}
              {store.slotInterval && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 0.75, sm: 1 },
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px dashed ${BRAND.line}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('stores.view.slotInterval')}
                  </Typography>
                  <Chip
                    label={t(`storeRegistration.basicInfo.slotInterval${store.slotInterval}`)}
                    size="small"
                    sx={{ backgroundColor: brandAlpha('accent', 0.12), color: BRAND.accentText, fontWeight: 600, fontSize: '0.75rem' }}
                  />
                </Box>
              )}
              {store.timeZone && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 0.75, sm: 1 },
                    mt: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('stores.view.timeZone')}
                  </Typography>
                  <Chip
                    label={store.timeZone}
                    size="small"
                    sx={{
                      backgroundColor: brandAlpha('text', 0.08),
                      color: BRAND.text,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">{t('common.notProvided')}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default StoresMainPanel;
